/**
 * Public marketing layout — shared shell for Landing, Features, HowItWorks,
 * FAQ, Blog, About, Privacy, Terms, Help.  Contains a fixed top nav and a
 * footer with internal links. Auth-aware CTA (Sign in / Go to Dashboard).
 *
 * Kept intentionally lightweight so it never affects the authenticated app.
 */
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_VERSION } from "@/config/version";

const NAV = [
  { to: "/",              label: "Home" },
  { to: "/features",      label: "Features" },
  { to: "/how-it-works",  label: "How it works" },
  { to: "/blog",          label: "Blog" },
  { to: "/faq",           label: "FAQ" },
  { to: "/about",         label: "About" },
];

function NavItem({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "rounded-full px-3 py-1.5 text-[13.5px] font-medium transition-colors",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-foreground/75 hover:bg-muted hover:text-foreground",
        )
      }
      data-testid={`public-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {label}
    </NavLink>
  );
}

export default function PublicLayout({ inlineChild }) {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const authed = !!user && user !== false;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70" data-testid="public-header">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-brand-link">
            <img
              src="/icons/brand-mark.png"
              alt="Expense & Save"
              className="h-10 w-10 shrink-0 rounded-2xl shadow-[0_8px_18px_-10px_rgba(30,63,51,0.55)] dark:shadow-[0_8px_20px_-8px_rgba(0,0,0,0.65)]"
              width="40"
              height="40"
              loading="eager"
              decoding="async"
            />
            <div className="min-w-0">
              <div className="font-display text-[16px] font-extrabold leading-tight tracking-tight">Expense &amp; Save</div>
              <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Personal finance, distilled</div>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {NAV.map((n) => <NavItem key={n.to} {...n} />)}
          </nav>

          <div className="flex items-center gap-2">
            {authed ? (
              <Link
                to="/dashboard"
                className="hidden rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 md:inline-flex"
                data-testid="public-cta-dashboard"
              >
                Open dashboard
              </Link>
            ) : (
              <Link
                to="/auth"
                className="hidden rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 md:inline-flex"
                data-testid="public-cta-signin"
              >
                Sign in
              </Link>
            )}
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/70 text-foreground/80 md:hidden"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((s) => !s)}
              data-testid="public-nav-toggle"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-border/60 bg-background md:hidden" data-testid="public-mobile-nav">
            <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
              {NAV.map((n) => <NavItem key={n.to} {...n} onClick={() => setMobileOpen(false)} />)}
              {authed ? (
                <Link to="/dashboard" className="mt-1 rounded-full bg-primary px-4 py-2 text-center text-[13px] font-semibold text-primary-foreground" data-testid="public-cta-dashboard-mobile">Open dashboard</Link>
              ) : (
                <Link to="/auth" className="mt-1 rounded-full bg-primary px-4 py-2 text-center text-[13px] font-semibold text-primary-foreground" data-testid="public-cta-signin-mobile">Sign in</Link>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12" data-testid="public-main">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-muted/20" data-testid="public-footer">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 md:grid-cols-4 md:px-6">
          <div>
            <div className="font-display text-lg font-bold">Expense &amp; Save</div>
            <p className="mt-2 text-[13px] text-muted-foreground">
              A calm personal finance platform. Track every dollar. Set a monthly budget. Let AI help
              you save.
            </p>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/80">Product</div>
            <ul className="mt-3 space-y-1.5 text-[13.5px]">
              <li><Link className="text-muted-foreground hover:text-foreground" to="/features">Features</Link></li>
              <li><Link className="text-muted-foreground hover:text-foreground" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-muted-foreground hover:text-foreground" to="/faq">FAQ</Link></li>
              <li><Link className="text-muted-foreground hover:text-foreground" to="/blog">Blog &amp; guides</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/80">Company</div>
            <ul className="mt-3 space-y-1.5 text-[13.5px]">
              <li><Link className="text-muted-foreground hover:text-foreground" to="/about">About</Link></li>
              <li><Link className="text-muted-foreground hover:text-foreground" to="/help">Help &amp; support</Link></li>
              <li><a className="text-muted-foreground hover:text-foreground" href="mailto:info@dailyexpense.online">info@dailyexpense.online</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/80">Legal</div>
            <ul className="mt-3 space-y-1.5 text-[13.5px]">
              <li><Link className="text-muted-foreground hover:text-foreground" to="/privacy">Privacy Policy</Link></li>
              <li><Link className="text-muted-foreground hover:text-foreground" to="/terms">Terms &amp; Conditions</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border/50 py-4">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-4 text-[12px] text-muted-foreground md:flex-row md:px-6">
            <span>© {new Date().getFullYear()} Expense &amp; Save. All rights reserved.</span>
            <span>v{APP_VERSION}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
